<template>
  <div v-if="visible" class="image-viewer-overlay" @click="handleOverlayClick">
    <div class="image-viewer-modal" @click.stop>
      <!-- 关闭按钮 -->
      <button class="close-button" @click="close" aria-label="关闭图片查看器">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- 图片容器 -->
      <div class="image-container">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="error" class="error-state">
          <div class="error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p>图片加载失败</p>
          <button class="retry-button" @click="retryLoad">重试</button>
        </div>

        <!-- 图片显示 -->
        <img 
          v-else
          :src="currentImageUrl" 
          :alt="title || '图片'"
          class="main-image"
          @load="handleImageLoad"
          @error="handleImageError"
        />
      </div>

      <!-- 导航按钮 (多图片时显示) -->
      <div v-if="images.length > 1" class="navigation">
        <button 
          class="nav-button prev-button" 
          @click="showPrevious"
          :disabled="currentIndex === 0"
          aria-label="上一张图片"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        
        <button 
          class="nav-button next-button" 
          @click="showNext"
          :disabled="currentIndex === images.length - 1"
          aria-label="下一张图片"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- 图片索引显示 (多图片时显示) -->
      <div v-if="images.length > 1" class="image-counter">
        {{ currentIndex + 1 }} / {{ images.length }}
      </div>

      <!-- 标题 (如果提供) -->
      <div v-if="title" class="image-title">
        {{ title }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    images: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every(item => typeof item === 'string')
      }
    },
    initialIndex: {
      type: Number,
      default: 0,
      validator: (value) => {
        return value >= 0
      }
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentIndex: 0,
      loading: false,
      error: false
    }
  },
  computed: {
    currentImageUrl() {
      if (this.images.length === 0) return ''
      return this.images[this.currentIndex] || ''
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentIndex = Math.max(0, Math.min(this.initialIndex, this.images.length - 1))
        this.loadCurrentImage()
        this.addEventListeners()
        this.preventBodyScroll()
      } else {
        this.removeEventListeners()
        this.restoreBodyScroll()
      }
    },
    currentIndex() {
      if (this.visible) {
        this.loadCurrentImage()
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    
    showPrevious() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.$emit('indexChange', this.currentIndex)
      }
    },
    
    showNext() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++
        this.$emit('indexChange', this.currentIndex)
      }
    },
    
    handleOverlayClick() {
      this.close()
    },
    
    loadCurrentImage() {
      if (!this.currentImageUrl) return
      
      this.loading = true
      this.error = false
      
      // 预加载图片
      const img = new Image()
      img.onload = () => {
        this.loading = false
        this.error = false
      }
      img.onerror = () => {
        this.loading = false
        this.error = true
      }
      img.src = this.currentImageUrl
    },
    
    handleImageLoad() {
      this.loading = false
      this.error = false
    },
    
    handleImageError() {
      this.loading = false
      this.error = true
    },
    
    retryLoad() {
      this.loadCurrentImage()
    },
    
    handleKeydown(event) {
      if (!this.visible) return
      
      switch (event.key) {
        case 'Escape':
          this.close()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.showPrevious()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.showNext()
          break
      }
    },
    
    addEventListeners() {
      document.addEventListener('keydown', this.handleKeydown)
    },
    
    removeEventListeners() {
      document.removeEventListener('keydown', this.handleKeydown)
    },
    
    preventBodyScroll() {
      document.body.style.overflow = 'hidden'
    },
    
    restoreBodyScroll() {
      document.body.style.overflow = ''
    }
  },
  
  beforeDestroy() {
    this.removeEventListeners()
    this.restoreBodyScroll()
  }
}
</script>

<style scoped>
.image-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10100;
  opacity: 0;
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.image-viewer-modal {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.close-button {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background-color 0.2s;
  z-index: 10101;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.image-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  min-width: 200px;
}

.main-image {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: opacity 0.3s ease;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  min-height: 200px;
  min-width: 200px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  color: #ff6b6b;
  margin-bottom: 16px;
}

.retry-button {
  margin-top: 16px;
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #0056b3;
}

.navigation {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  padding: 0 20px;
}

.nav-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s;
  pointer-events: auto;
}

.nav-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.image-counter {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}

.image-title {
  position: absolute;
  bottom: -80px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  max-width: 80vw;
  word-wrap: break-word;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .image-viewer-modal {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .main-image {
    max-width: 95vw;
    max-height: 85vh;
  }
  
  .close-button {
    top: -40px;
    right: -10px;
    width: 36px;
    height: 36px;
  }
  
  .nav-button {
    width: 44px;
    height: 44px;
  }
  
  .navigation {
    padding: 0 10px;
  }
  
  .image-counter {
    bottom: -30px;
    font-size: 12px;
    padding: 6px 12px;
  }
  
  .image-title {
    bottom: -60px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .main-image {
    max-width: 100vw;
    max-height: 90vh;
  }
  
  .close-button {
    top: 10px;
    right: 10px;
    position: fixed;
  }
  
  .navigation {
    padding: 0 5px;
  }
  
  .nav-button {
    width: 40px;
    height: 40px;
  }
}
</style>